import { JourneySectionProps } from './JourneySection';
import {
  Container,
  containerPaddingRem,
  containerWidthRem,
} from '@exploration/shared-components';
import { desktop, tablet } from '@thrivent/midwest-shared';
import styled, { StyledProps } from 'styled-components';

interface BaseProps {
  forceMobileBreakpoint?: boolean;
}

// props that include both isStacked and all BaseProps
type StackedRulesProps = Pick<JourneySectionProps, 'isStacked'> & BaseProps;

/**
 * Defined a unique layout for the hero section
 */
const stackedRules = ({ forceMobileBreakpoint }: StackedRulesProps) =>
  `
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr auto;
    padding: 0;

    & ${Indicator} {
      grid-area: item2;
      position: absolute;
      top: 1rem;
      z-index: 1;
      margin-left: 1rem;
      &::before {
        display: none;
      }
    }
    & > *:nth-child(2) {
      grid-area: item1;
    }
    & > *:nth-child(3) {
      grid-area: item3;
    }

    grid-template-areas:
      'item1'
      'item2'
      'item3';

    ${
      !forceMobileBreakpoint &&
      `
      ${tablet} {
        grid-template-columns: 1fr 1fr;
        padding: 0 0 0 2rem;
  
        & ${Indicator} {
          position: relative; /* mobile+ reset - position */
          top: 0; /* mobile+ reset - position */
          z-index: unset; /* mobile+ reset - z-index to appear in front of dotted-line pseudo-element */
          margin-left: 0; /* mobile+ reset - margin alignment */
          &::before {
            display: flex; /* mobile+ reset - display of dotted-line pseudo-element */
          }
        }
        & > *:nth-child(2) {
          align-self: flex-end;
        }
        grid-template-areas:
          'item1 item3'
          'item2 item3';
      }
  
      ${desktop} {
        padding: 0 0 0 4rem;
        max-width: calc(
          ${containerWidthRem}rem + 4rem + ${containerPaddingRem}rem + 1rem
        ); /* container width + padding + padding-inline(container) + grid gap */
        margin: 0;
        margin-left: max(0px, (100% - ${containerWidthRem}rem)/2);
      }
    `
    }
  `;

type WrapperProps = Pick<JourneySectionProps, 'isStacked' | 'isStart'> &
  BaseProps;

export const Wrapper = styled(Container)<StyledProps<WrapperProps>>`
  display: flex;
  position: relative; /* sets the position context for z-index rule */
  z-index: 0; /* ensure the dashed line appears on top of the container */
  overflow: hidden; /* clips the dotted-line at the edges */
  padding: 3rem 1rem;
  gap: 1rem;

  ${({ forceMobileBreakpoint }) =>
    !forceMobileBreakpoint &&
    `
      ${tablet} {
        padding: 4rem 2rem;
        & *:nth-child(2) {
          flex-grow: 1;
        }
      }
    `}

  ${({ forceMobileBreakpoint }) =>
    !forceMobileBreakpoint &&
    `
      ${desktop} {
        padding: 7rem 4rem;
      }
    `}

  ${({ isStacked }) => isStacked && stackedRules}
`;

type IndicatorProps = Pick<
  JourneySectionProps,
  'isStart' | 'isEnd' | 'isInverse'
> &
  BaseProps & { isActive: boolean; isPast: boolean };

export const Indicator = styled.div<StyledProps<IndicatorProps>>`
  position: relative; /* sets the position context for positioning the dotted-line pseudo-element */
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  font-size: 1rem;
  border: 2px solid;
  background-color: ${({ isInverse, theme }) =>
    isInverse ? 'white' : theme.midwestColors.identityBrandPrimaryMedium};
  border-color: ${({ isInverse, theme }) =>
    isInverse ? theme.midwestColors.identityBrandPrimaryMedium : 'white'};
  color: ${({ isInverse, theme }) =>
    isInverse ? theme.midwestColors.identityBrandPrimaryMedium : 'white'};
  border-radius: 50%;
  margin-bottom: ${({ isStart }) => (isStart ? '2rem' : 0)};
  box-sizing: border-box;
  font-weight: bold;
  &::before {
    content: '';
    position: absolute;
    height: 200vh;
    width: 2px;
    margin-top: ${({ isStart, isEnd }) =>
      isStart ? 0 : isEnd ? '-200vh' : '-100vh'};
    background-image: url('data:image/svg+xml,<svg width="2" height="2500" xmlns="http://www.w3.org/2000/svg"><line x1="0" y1="0" x2="0" y2="2500" stroke="%232e66ff" stroke-width="3" stroke-dasharray="8,5" /></svg>');
    z-index: -2;
    top: 0;
  }

  &::after {
    content: '';
    display: ${({ isInverse }) => (isInverse ? 'none' : 'block')};
    position: absolute;
    height: 100%;
    width: 100%;
    z-index: -1;
    top: 0;
    background-color: white;
    box-sizing: border-box;
    border: 2px solid #2e66ff;
    border-radius: 50%;
    transform: ${({ isActive, isEnd, isPast }) =>
      isActive || (isEnd && isPast) ? 'scale(1.5)' : 'scale(1)'};
    @media (prefers-reduced-motion: no-preference) {
      transition: all 0.1s ease-out;
    }
  }

  ${({ forceMobileBreakpoint }) =>
    !forceMobileBreakpoint &&
    `
      ${tablet} {
        width: 40px;
        height: 40px;
        font-size: 1.5rem;
      }
    `}
`;

export const IndicatorContent = styled.div<
  Pick<IndicatorProps, 'isEnd' | 'isPast'>
>`
  display: ${({ isEnd, isPast }) => (isEnd && isPast ? 'none' : 'flex')};
  align-items: center;
  justify-content: center;
`;

export const AltSymbol = styled.div<Pick<IndicatorProps, 'isEnd' | 'isPast'>>`
  display: ${({ isEnd, isPast }) => (isEnd && isPast ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  transform: rotate(-90deg);
`;
